'use client';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { LOYALTY_QUERIES } from '../../infrastructure/loyalty/utils/queries';
import type {
  GetCustomerBalanceRequest,
  GetTransactionHistoryRequest,
  GetLoyaltyPolicyRequest,
} from '../../infrastructure/loyalty/utils/types';
import { LOYALTY_QUERY_KEYS } from '../../infrastructure/loyalty/utils/keys';
import { loyaltyAPIClient } from '../../infrastructure/loyalty/loyaltyAPIClient';

export const useLoyaltyService = () => {
  const useGetCustomerBalance = (
    payload: GetCustomerBalanceRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getCustomerBalance(requestPayload),
      enabled,
    });
  };

  const useGetTransactionHistory = (
    payload: GetTransactionHistoryRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getTransactionHistory(requestPayload),
      enabled,
    });
  };

  const useGetTransactionHistoryInfinite = (
    payload: Omit<GetTransactionHistoryRequest, 'lastEvaluatedKey'> & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useInfiniteQuery({
      queryKey: LOYALTY_QUERY_KEYS.getTransactionHistory({
        ...requestPayload,
        lastEvaluatedKey: undefined,
      }),
      queryFn: ({ pageParam }) => {
        return loyaltyAPIClient.getTransactionHistory({
          ...requestPayload,
          lastEvaluatedKey: pageParam,
        });
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey,
      enabled,
    });
  };

  const useGetLoyaltyPolicy = (
    payload: GetLoyaltyPolicyRequest & {
      enabled?: boolean;
    },
  ) => {
    const { enabled, ...requestPayload } = payload;
    return useQuery({
      ...LOYALTY_QUERIES.getLoyaltyPolicy(requestPayload),
      enabled,
    });
  };

  return {
    useGetCustomerBalance,
    useGetTransactionHistory,
    useGetTransactionHistoryInfinite,
    useGetLoyaltyPolicy,
  };
};
